import type { RefObject } from 'preact';
import { useMemo } from 'preact/hooks';

import type { TemplateContextMap, TemplateFunction, TemplateResult } from 'Components/types';
import { createSubFacetsSelector } from 'Core/selectors/search';
import { useSelector } from 'Core/store';
import { Facet } from 'Models/index';
import facetsConfig from 'Models/uiConfig/facetsConfig';
import { cloneSafe } from 'Utils/components';
import { simpleHandler } from 'Utils/roleHandler';

import type { FacetCommonProps } from '../baseFacet';
import RangedFacet from './rangedFacet';

const sizeFacetDefaults = facetsConfig.getNamedConfig('sizeFacet');

export default function SizeFacet({
  facet: { field },
  selection,
  config: { slider, step = sizeFacetDefaults?.step },
  templateFunc,
  facetRef,
  commonParams,
  commonRoles,
}: FacetCommonProps): TemplateResult {
  const subFacetsSelector = useMemo(() => createSubFacetsSelector(field), [field]);
  const subFacets = useSelector(subFacetsSelector);

  if (!subFacets.some((f) => f instanceof Facet)) {
    return null;
  }

  const onClick = simpleHandler(commonRoles);

  const subFacetComponents = subFacets.reduce((map, subFacet) => {
    const { field: subField } = subFacet;
    const SubFacetFunc =
      subFacet instanceof Facet
        ? (templ: TemplateFunction<TemplateContextMap>) => {
            const props = {
              facet: subFacet,
              field: subField,
              selection,
              allowEqualBounds: true,
              templateFunc: templ,
              facetRef: { current: null } as RefObject<HTMLElement>,
              commonParams: { ...subFacet },
              commonRoles: {},
              config: { step, slider },
            };
            return <RangedFacet {...(props as FacetCommonProps)} key={subField} />;
          }
        : () => null;

    return { ...map, [`${subField}Facet`]: SubFacetFunc };
  }, {});

  const component = templateFunc.call({
    ...commonParams,
    ...subFacetComponents,
  });
  return cloneSafe(component, facetRef, { onClick });
}
